/**
 * useSocket Hook
 * Manages WebSocket connection to the simulation server
 * Supports multiplayer with authentication via cookies
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import type {
  WorldState,
  SimulationStatus,
  CreatureDetails,
  BrainData,
  UserSession,
  PlayersUpdate,
  MyCreatureState,
  GuidanceSettings,
} from '../types/simulation';

// Determine WebSocket URL based on environment
const getSocketUrl = () => {
  // Use environment variable if set
  if (import.meta.env.VITE_SOCKET_URL) {
    return import.meta.env.VITE_SOCKET_URL;
  }
  // In production, use secure WebSocket on same host
  if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
    return `wss://ws.${window.location.host.replace('genesis.', '')}`;
  }
  // Development default
  return 'http://localhost:4001';
};

const SOCKET_URL = getSocketUrl();

interface UseSocketOptions {
  autoConnect?: boolean;
  onWorldState?: (state: WorldState) => void;
  onSimulationStatus?: (status: SimulationStatus) => void;
  onCreatureDetails?: (details: CreatureDetails | null) => void;
  onCreatureBrain?: (brain: BrainData | null) => void;
  onSessionState?: (session: UserSession) => void;
  onPlayersUpdate?: (update: PlayersUpdate) => void;
  onMyCreature?: (state: MyCreatureState) => void;
  onGuidanceUpdated?: (guidance: GuidanceSettings) => void;
  onWaypointSet?: (point: { x: number; y: number }) => void;
  onWaypointCleared?: () => void;
  onError?: (error: { message: string }) => void;
}

interface UseSocketReturn {
  socket: Socket | null;
  isConnected: boolean;
  connect: () => void;
  disconnect: () => void;
  // Subscriptions
  subscribeToState: () => void;
  unsubscribeFromState: () => void;
  // Simulation control
  startSimulation: () => void;
  pauseSimulation: () => void;
  stopSimulation: () => void;
  resetSimulation: () => void;
  stepSimulation: () => void;
  setSpeed: (speed: number) => void;
  // Creature actions
  selectCreature: (id: string) => void;
  deselectCreature: () => void;
  spawnMyCreature: (x?: number, y?: number) => void;
  spawnFood: (x: number, y: number) => void;
  // Guidance
  setGuidance: (guidance: Partial<GuidanceSettings>) => void;
  setWaypoint: (x: number, y: number) => void;
  clearWaypoint: () => void;
  // Request data
  requestStats: () => void;
  requestStatus: () => void;
  requestPlayers: () => void;
  requestLeaderboard: () => void;
}

export function useSocket(options: UseSocketOptions = {}): UseSocketReturn {
  const {
    autoConnect = true,
    onWorldState,
    onSimulationStatus,
    onCreatureDetails,
    onCreatureBrain,
    onSessionState,
    onPlayersUpdate,
    onMyCreature,
    onGuidanceUpdated,
    onWaypointSet,
    onWaypointCleared,
    onError,
  } = options;

  const socketRef = useRef<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  // Store callbacks in refs to avoid reconnection on callback change
  const callbacksRef = useRef({
    onWorldState,
    onSimulationStatus,
    onCreatureDetails,
    onCreatureBrain,
    onSessionState,
    onPlayersUpdate,
    onMyCreature,
    onGuidanceUpdated,
    onWaypointSet,
    onWaypointCleared,
    onError,
  });

  useEffect(() => {
    callbacksRef.current = {
      onWorldState,
      onSimulationStatus,
      onCreatureDetails,
      onCreatureBrain,
      onSessionState,
      onPlayersUpdate,
      onMyCreature,
      onGuidanceUpdated,
      onWaypointSet,
      onWaypointCleared,
      onError,
    };
  }, [
    onWorldState,
    onSimulationStatus,
    onCreatureDetails,
    onCreatureBrain,
    onSessionState,
    onPlayersUpdate,
    onMyCreature,
    onGuidanceUpdated,
    onWaypointSet,
    onWaypointCleared,
    onError,
  ]);

  // Connect to socket
  const connect = useCallback(() => {
    if (socketRef.current?.connected) return;

    const socket = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      withCredentials: true, // Send cookies for authentication
    });

    socket.on('connect', () => {
      console.log('[Socket] Connected:', socket.id);
      setIsConnected(true);

      // Join session (authentication via cookie)
      socket.emit('session:join');
    });

    socket.on('disconnect', (reason) => {
      console.log('[Socket] Disconnected:', reason);
      setIsConnected(false);
    });

    socket.on('connect_error', (error) => {
      console.error('[Socket] Connection error:', error);
      setIsConnected(false);
    });

    // ========== Event handlers ==========

    socket.on('world:state', (state: WorldState) => {
      callbacksRef.current.onWorldState?.(state);
    });

    socket.on('simulation:status', (status: SimulationStatus) => {
      callbacksRef.current.onSimulationStatus?.(status);
    });

    socket.on('creature:details', (details: CreatureDetails | null) => {
      callbacksRef.current.onCreatureDetails?.(details);
    });

    socket.on('creature:brain', (brain: BrainData | null) => {
      callbacksRef.current.onCreatureBrain?.(brain);
    });

    // Session events
    socket.on('session:state', (session: UserSession) => {
      callbacksRef.current.onSessionState?.(session);
    });

    socket.on('session:error', (error: { message: string }) => {
      console.error('[Socket] Session error:', error.message);
      callbacksRef.current.onError?.(error);
    });

    // Players events
    socket.on('players:update', (update: PlayersUpdate) => {
      callbacksRef.current.onPlayersUpdate?.(update);
    });

    socket.on('players:list', (players: PlayersUpdate['players']) => {
      callbacksRef.current.onPlayersUpdate?.({
        players,
        leaderboard: { oldest: [], mostOffspring: [], highestEnergy: [] },
        connectedCount: players.filter((p) => p.isConnected).length,
      });
    });

    // My creature events
    socket.on('creature:myCreature', (state: MyCreatureState) => {
      callbacksRef.current.onMyCreature?.(state);
    });

    // Guidance events
    socket.on('guidance:updated', (guidance: GuidanceSettings) => {
      callbacksRef.current.onGuidanceUpdated?.(guidance);
    });

    socket.on('waypoint:set', (point: { x: number; y: number }) => {
      callbacksRef.current.onWaypointSet?.(point);
    });

    socket.on('waypoint:cleared', () => {
      callbacksRef.current.onWaypointCleared?.();
    });

    // Error events
    socket.on('error', (error: { message: string }) => {
      console.error('[Socket] Error:', error.message);
      callbacksRef.current.onError?.(error);
    });

    socketRef.current = socket;
  }, []);

  // Disconnect from socket
  const disconnect = useCallback(() => {
    if (socketRef.current) {
      // Remove all event listeners before disconnecting to prevent memory leaks
      const socket = socketRef.current;
      socket.off('connect');
      socket.off('disconnect');
      socket.off('connect_error');
      socket.off('world:state');
      socket.off('simulation:status');
      socket.off('creature:details');
      socket.off('creature:brain');
      socket.off('session:state');
      socket.off('session:error');
      socket.off('players:update');
      socket.off('players:list');
      socket.off('creature:myCreature');
      socket.off('guidance:updated');
      socket.off('waypoint:set');
      socket.off('waypoint:cleared');
      socket.off('error');

      socket.disconnect();
      socketRef.current = null;
      setIsConnected(false);
    }
  }, []);

  // Auto-connect on mount
  useEffect(() => {
    if (autoConnect) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [autoConnect, connect, disconnect]);

  // === Subscriptions ===

  const subscribeToState = useCallback(() => {
    socketRef.current?.emit('subscribe:state');
  }, []);

  const unsubscribeFromState = useCallback(() => {
    socketRef.current?.emit('unsubscribe:state');
  }, []);

  // === Simulation Control ===

  const startSimulation = useCallback(() => {
    socketRef.current?.emit('simulation:start');
  }, []);

  const pauseSimulation = useCallback(() => {
    socketRef.current?.emit('simulation:pause');
  }, []);

  const stopSimulation = useCallback(() => {
    socketRef.current?.emit('simulation:stop');
  }, []);

  const resetSimulation = useCallback(() => {
    socketRef.current?.emit('simulation:reset');
  }, []);

  const stepSimulation = useCallback(() => {
    socketRef.current?.emit('simulation:step');
  }, []);

  const setSpeed = useCallback((speed: number) => {
    socketRef.current?.emit('simulation:speed', speed);
  }, []);

  // === Creature Actions ===

  const selectCreature = useCallback((id: string) => {
    socketRef.current?.emit('creature:select', id);
  }, []);

  const deselectCreature = useCallback(() => {
    socketRef.current?.emit('creature:deselect');
  }, []);

  const spawnMyCreature = useCallback((x?: number, y?: number) => {
    const data = x !== undefined && y !== undefined ? { x, y } : undefined;
    socketRef.current?.emit('creature:spawnMine', data);
  }, []);

  const spawnFood = useCallback((x: number, y: number) => {
    socketRef.current?.emit('food:spawn', { x, y });
  }, []);

  // === Guidance ===

  const setGuidance = useCallback((guidance: Partial<GuidanceSettings>) => {
    socketRef.current?.emit('creature:setGuidance', guidance);
  }, []);

  const setWaypoint = useCallback((x: number, y: number) => {
    socketRef.current?.emit('creature:setWaypoint', { x, y });
  }, []);

  const clearWaypoint = useCallback(() => {
    socketRef.current?.emit('creature:clearWaypoint');
  }, []);

  // === Request Data ===

  const requestStats = useCallback(() => {
    socketRef.current?.emit('request:stats');
  }, []);

  const requestStatus = useCallback(() => {
    socketRef.current?.emit('request:status');
  }, []);

  const requestPlayers = useCallback(() => {
    socketRef.current?.emit('request:players');
  }, []);

  const requestLeaderboard = useCallback(() => {
    socketRef.current?.emit('request:leaderboard');
  }, []);

  return {
    socket: socketRef.current,
    isConnected,
    connect,
    disconnect,
    subscribeToState,
    unsubscribeFromState,
    startSimulation,
    pauseSimulation,
    stopSimulation,
    resetSimulation,
    stepSimulation,
    setSpeed,
    selectCreature,
    deselectCreature,
    spawnMyCreature,
    spawnFood,
    setGuidance,
    setWaypoint,
    clearWaypoint,
    requestStats,
    requestStatus,
    requestPlayers,
    requestLeaderboard,
  };
}
